"use client";
import { logoStyles, searchInputStyles } from "@components/layouts/navigation/styles";
import { SEARCH_OVERLAY, SEARCHBAR_INPUT } from "@e2e/helpers/homepage/constants";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { SearchAnalyticsType } from "@lib/analytics/analyticsTypes";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { IconButton } from "@ecco/ui/icon-button";
import { Input } from "@ecco/ui/input";
import { InputLeftElement } from "@ecco/ui/input-element";
import { InputGroup } from "@ecco/ui/input-group";
import type { BoxProps } from "@ecco/ui/box";
import { Box } from "@ecco/ui/box";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { useBreakpoint } from "@ecco/ui/use-breakpoint";
import { useEffect, useMemo, useRef } from "react";
import { shadows, sizes, transitions } from "@ecco/ui/tokens";
import { AutoCompletePanel } from "../components/autocomplete-panel/AutoCompletePanel";
import { useSearchBar } from "./useSearchBar";
import type { SystemStyleObject } from "@ecco/ui/types";
import { Logo } from "../components/Logo";
import { Flex } from "@ecco/ui/flex";
import { useRouteChange } from "@store/hooks/useRouteChange";
import { Portal } from "@ecco/ui/portal";

interface SearchBarProps extends BoxProps {
    searchBarOpen?: boolean;
    onClose?: () => void;
}

export const SearchBar = ({ searchBarOpen, onClose }: SearchBarProps) => {
    const { get: getMicrocopy } = useMicrocopy();
    const lg = useBreakpoint("lg");
    const isDesktopView = lg;

    const {
        autocomplete,
        recentSearchesPlugin,
        containerRef,
        formRef,
        inputRef,
        isLoading,
        setIsLoading,
        autocompleteState,
    } = useSearchBar({ searchBarOpen, isDesktopView });

    // useRouteChange fires on mount too, which would close the bar right after it opens.
    // Skip until the url actually changes
    const lastRouteUrlRef = useRef<string | null>(null);
    useRouteChange((url) => {
        if (lastRouteUrlRef.current === url) {
            return;
        }
        const isInitialMount = lastRouteUrlRef.current === null;
        lastRouteUrlRef.current = url;
        if (isInitialMount) {
            return;
        }
        setIsLoading(false);
        onClose?.();
    });

    // Focus input when search bar opens
    useEffect(() => {
        if (!searchBarOpen || !inputRef.current) {
            return;
        }
        inputRef.current?.focus();
    }, [inputRef, searchBarOpen]);

    const formProps = useMemo(
        () =>
            autocomplete?.getFormProps({
                inputElement: inputRef.current,
            }),
        [autocomplete, inputRef]
    );

    const inputProps = useMemo(
        () =>
            autocomplete?.getInputProps({
                inputElement: inputRef?.current,
                maxLength: 100,
                value: autocompleteState?.query,
            }),
        [autocomplete, inputRef, autocompleteState]
    );

    // Track search queries with the analytics client
    useEffect(() => {
        let analyticsTimeout: ReturnType<typeof setTimeout>;
        if (autocompleteState?.query) {
            analyticsTimeout = setTimeout(() => {
                AnalyticsClient.saveSearch(autocompleteState?.query);
            }, 300);
        }
        return () => clearTimeout(analyticsTimeout);
    }, [autocompleteState?.query]);

    // Instant search: push to dataLayer 5s after the user stops typing while results are shown.
    useEffect(() => {
        let analyticsTimeout: ReturnType<typeof setTimeout>;
        const searchTerm = autocompleteState?.query?.trim();

        if (searchBarOpen && autocompleteState?.isOpen && searchTerm) {
            analyticsTimeout = setTimeout(() => {
                AnalyticsClient.trackSearch(searchTerm, SearchAnalyticsType.instant);
            }, 5000);
        }

        return () => clearTimeout(analyticsTimeout);
    }, [autocompleteState?.query, autocompleteState?.isOpen, searchBarOpen]);

    const containerStyles: SystemStyleObject = useMemo(() => {
        const base: SystemStyleObject = {
            position: "fixed",
            visibility: "hidden",
            transformOrigin: "center",
            transitionProperty: "opacity, visibility",
            // Offset the time it takes for visibility to change with delay. Visibility can't animate so has to be instant.
            transitionDelay: `0s, ${transitions.duration.fast}`,
            transitionDuration: `${transitions.duration.fast}, 0s`,
            transitionTimingFunction: transitions.easing["ease-out"],
            pointerEvents: "none",
            opacity: 0,
            zIndex: 0,
            left: 0,
            right: 0,
            top: 0,
            backgroundColor: "white",
            padding: {
                base: 4,
                lg: 0,
            },
            height: {
                base: "100dvh",
                lg: "85dvh",
            },
            width: "100%",
        };

        if (searchBarOpen) {
            return {
                ...base,
                visibility: "visible",
                pointerEvents: "auto",
                transitionDelay: `0s, 0s`,
                // Must be above overlay (9) and nav (11) since it's now portal-rendered
                zIndex: 12,
                opacity: 1,
            };
        }

        return base;
    }, [searchBarOpen]);

    // Handle closing the search bar with the Escape key
    useEffect(() => {
        if (!searchBarOpen) return;
        const handleKeydown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose?.();
            }
        };

        document.addEventListener("keydown", handleKeydown);
        return () => {
            document.removeEventListener("keydown", handleKeydown);
        };
    }, [onClose, searchBarOpen]);

    return (
        // Portal renders the search panel directly into document.body,
        // escaping the nav's stacking context (position: sticky, zIndex: 11)
        // so the overlay in Navigation.tsx correctly appears behind it.
        <Portal>
            <Box
                className="aa-Autocomplete"
                ref={containerRef}
                {...autocomplete?.getRootProps()}
                sx={containerStyles}
                data-testid={SEARCH_OVERLAY}
            >
                <Flex
                    as="form"
                    ref={formRef}
                    className="aa-Form"
                    display="flex"
                    maxW="container.largeDesktop"
                    mx="auto"
                    w="100%"
                    // Chakra polymorphic component doesn't have proper type inference with the element used.
                    // As a result the <Flex as="form" /> component expects to match a <div> element and not a <form> element.
                    {...(formProps as any)}
                    position="relative"
                    boxShadow={{ base: "none", lg: shadows.fromBottom }}
                >
                    <Flex
                        sx={{
                            ...logoStyles,
                            display: {
                                base: "none",
                                lg: "flex",
                            },
                        }}
                    >
                        <Logo />
                    </Flex>
                    <InputGroup className="aa-InputWrapper" w="100%" bg="white">
                        <InputLeftElement
                            w={sizes.icon["4xs"]}
                            h={sizes.navBarCollapsedHeight}
                            zIndex={3}
                            position="absolute"
                            tabIndex={-1}
                        >
                            <ECCOIcon name="search" aria-label="Search" />
                        </InputLeftElement>
                        <Input
                            className="aa-Input"
                            ref={inputRef}
                            display={"flex"}
                            {...(inputProps as any)}
                            {...searchInputStyles}
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            data-testid={SEARCHBAR_INPUT}
                        />
                    </InputGroup>
                    {searchBarOpen && (
                        <Box
                            width="10.4"
                            display="flex"
                            justifyContent="center"
                            zIndex={2}
                            position="absolute"
                            right={0}
                            top={0}
                        >
                            <IconButton
                                w="10.4"
                                height={{
                                    base: sizes.navBarCollapsedHeight,
                                    sm: sizes.navBarCollapsedHeight,
                                }}
                                zIndex={3}
                                aria-label={getMicrocopy(
                                    CommonCMS.shared,
                                    CommonCMS.ariaLabels.closeModalAria
                                )}
                                icon={<ECCOIcon name="close" />}
                                variant="solid"
                                onClick={() => {
                                    onClose?.();
                                }}
                            />
                        </Box>
                    )}
                </Flex>
                <AutoCompletePanel
                    autocompleteState={autocompleteState}
                    autocomplete={autocomplete}
                    isLoading={isLoading}
                    recentSearchesPlugin={recentSearchesPlugin}
                    searchBarOpen={searchBarOpen}
                />
            </Box>
        </Portal>
    );
};
